import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db, auth } from './firebase';
import Upgrade from './Upgrade';
import {
    ChevronLeftIcon,
    StarIcon,
    ChatBubbleLeftIcon,
    BookOpenIcon
} from '@heroicons/react/24/outline';

const VideoPlayer = ({ userData }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [video, setVideo] = useState(null);
    const [playlist, setPlaylist] = useState([]);
    const [loading, setLoading] = useState(true);

    const isPaid = userData?.isPaid || auth.currentUser?.email === process.env.REACT_APP_ADMIN_EMAIL;

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch active video
                const videoDoc = await getDoc(doc(db, 'videos', id));
                if (videoDoc.exists()) {
                    setVideo({ id: videoDoc.id, ...videoDoc.data() });
                }

                // Fetch all videos for sidebar playlist
                const querySnapshot = await getDocs(collection(db, 'videos'));
                const videosList = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Numerically sort playlist
                const sortedPlaylist = videosList.sort((a, b) => {
                    const numA = parseInt(a.title?.match(/\d+/)?.[0] || 0);
                    const numB = parseInt(b.title?.match(/\d+/)?.[0] || 0);
                    return numA - numB;
                });
                setPlaylist(sortedPlaylist);

            } catch (err) {
                console.error("Erro ao carregar dados do vídeo:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const getYouTubeVideoId = (url) => {
        if (!url) return null;
        const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/?|.*?[?&]v=))([^"&?/\s]{11})/);
        return match ? match[1] : null;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#020617] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    // Se NÃO for pago, exibe o Paywall (Upgrade)
    if (!loading && !isPaid) {
        return <Upgrade />;
    }

    if (!video) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold text-white mb-4">Vídeo não encontrado</h2>
                <button onClick={() => navigate('/dashboard/feed')} className="text-emerald-500 font-bold hover:underline italic">Voltar para o Feed</button>
            </div>
        );
    }

    return (
        <div className="pb-20 animate-fade-in font-sans">
            {/* Back Button and Title */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/dashboard/feed')}
                        className="p-3 bg-white/5 border border-white/10 rounded-2xl text-gray-400 hover:text-white hover:bg-white/10 transition-all"
                    >
                        <ChevronLeftIcon className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-black text-white italic tracking-tighter uppercase">{video.title}</h1>
                        <p className="text-emerald-500 text-xs font-black uppercase tracking-[0.2em] mt-1">Sessão Original TraderFlix</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/5 rounded-xl text-xs font-bold transition-all flex items-center gap-2">
                        <StarIcon className="w-4 h-4 text-emerald-500" /> Favoritar
                    </button>
                    <button className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition-all active:scale-95 shadow-lg shadow-emerald-500/20">
                        Próxima Aula
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Player Area */}
                <div className="lg:col-span-2 space-y-8">
                    <div className="aspect-video bg-black rounded-[2.5rem] overflow-hidden border border-white/5 shadow-3xl shadow-black relative group">
                        <iframe
                            width="100%"
                            height="100%"
                            src={`https://www.youtube.com/embed/${getYouTubeVideoId(video.youtubeLink)}?autoplay=1&rel=0&modestbranding=1`}
                            title={video.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                        ></iframe>
                    </div>

                    {/* Description & Notes */}
                    <div className="bg-white/5 border border-white/5 rounded-[2.5rem] p-8 space-y-6">
                        <div className="flex items-center gap-3">
                            <BookOpenIcon className="w-6 h-6 text-emerald-500" />
                            <h2 className="text-xl font-black text-white italic uppercase">Resumo da Aula</h2>
                        </div>
                        <p className="text-gray-400 leading-relaxed font-medium">
                            Nesta aula, exploramos profundamente os conceitos de <strong>{video.title}</strong> aplicados ao mercado atual.
                            Siga as diretrizes do PDF anexo para otimizar seus resultados e gerencie o risco em todas as entradas.
                        </p>
                        <div className="flex flex-wrap gap-4 pt-4 border-t border-white/5">
                            <button className="px-6 py-3 bg-white/5 border border-white/10 rounded-2xl text-sm font-bold hover:bg-white/10 transition-all flex items-center gap-2">
                                <ChatBubbleLeftIcon className="w-5 h-5" /> Suporte VIP
                            </button>
                            <button className="px-6 py-3 bg-white/5 border border-white/10 rounded-2xl text-sm font-bold hover:bg-white/10 transition-all">
                                Materiais PDF
                            </button>
                        </div>
                    </div>
                </div>

                {/* Sidebar Playlist */}
                <div className="space-y-6">
                    <div className="flex items-center justify-between px-2">
                        <h3 className="font-black text-xs uppercase tracking-[0.3em] text-emerald-500 italic">Próximos Episódios</h3>
                        <span className="text-[10px] font-bold text-gray-500 uppercase">{playlist.length} Aulas</span>
                    </div>
                    <div className="space-y-3 max-h-[1000px] overflow-y-auto scrollbar-hide">
                        {playlist.map((item, idx) => (
                            <Link
                                key={item.id}
                                to={`/dashboard/video/${item.id}`}
                                className={`flex gap-4 p-3 rounded-2xl border transition-all group ${item.id === video.id
                                    ? 'bg-emerald-500/10 border-emerald-500/20 shadow-[inset_0_0_15px_rgba(16,185,129,0.05)]'
                                    : 'bg-white/5 border-transparent hover:bg-white/10'
                                    }`}
                            >
                                <div className="w-24 aspect-video rounded-xl overflow-hidden bg-black flex-shrink-0 relative">
                                    <img
                                        src={`https://img.youtube.com/vi/${getYouTubeVideoId(item.youtubeLink)}/mqdefault.jpg`}
                                        alt={item.title}
                                        className={`w-full h-full object-cover ${item.id === video.id ? 'opacity-100' : 'opacity-40 group-hover:opacity-100'} transition-opacity`}
                                    />
                                    {item.id === video.id && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-emerald-500/20">
                                            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col justify-center overflow-hidden">
                                    <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${item.id === video.id ? 'text-emerald-400' : 'text-gray-500'}`}>Episódio {idx + 1}</p>
                                    <h4 className={`text-sm font-bold truncate ${item.id === video.id ? 'text-white' : 'text-gray-400 group-hover:text-white'} transition-colors underline-offset-4`}>{item.title}</h4>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoPlayer;
